import { ArrowLeft, Calendar, Clock, MessageSquare, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useState } from "react";

const AppointmentCalendar = () => {
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [comments, setComments] = useState("");
  const [isBooked, setIsBooked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Mock service data - would come from props/context in real app
  const service = {
    id: "1",
    title: "Mindful Life Coaching Session",
    provider: {
      name: "Sarah Moonlight",
      avatar: "https://images.pexels.com/photos/3763188/pexels-photo-3763188.jpeg?auto=compress&cs=tinysrgb&w=200"
    },
    duration: "90 min",
    price: 85
  };

  // Generate available dates (next 14 days, excluding weekends for this example)
  const generateAvailableDates = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 1; i <= 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      
      // Skip weekends (Saturday = 6, Sunday = 0)
      if (date.getDay() !== 0 && date.getDay() !== 6) {
        dates.push({
          value: date.toISOString().split('T')[0],
          label: date.toLocaleDateString('en-US', { 
            weekday: 'long', 
            month: 'long', 
            day: 'numeric' 
          })
        });
      }
    }
    
    return dates;
  };

  // Available time slots
  const availableTimeSlots = [
    { value: "10:00", label: "10:00 AM" },
    { value: "11:30", label: "11:30 AM" },
    { value: "13:00", label: "1:00 PM" },
    { value: "14:30", label: "2:30 PM" },
    { value: "16:00", label: "4:00 PM" }
  ];

  const availableDates = generateAvailableDates();

  const handleConfirmAppointment = async () => {
    if (!selectedDate || !selectedTime) {
      alert("Please select both date and time");
      return;
    }

    setIsLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      console.log("Appointment booked:", {
        serviceId: service.id,
        date: selectedDate,
        time: selectedTime,
        comments: comments
      });
      
      setIsLoading(false);
      setIsBooked(true);
      
      // TODO: Save appointment to database
      // TODO: Send notification to provider
    }, 1500);
  };

  const handleGoBack = () => {
    console.log("Going back to service detail");
    // TODO: Navigate back to service detail
  };

  const handleBookAnother = () => {
    setIsBooked(false);
    setSelectedDate("");
    setSelectedTime("");
    setComments("");
  };

  // Success screen
  if (isBooked) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: '#f6f1eb' }}>
        <Card className="w-full max-w-md border-0 shadow-xl bg-white/90 backdrop-blur-sm" style={{ border: '1px solid #efe7dd' }}>
          <CardContent className="p-8 text-center">
            <div className="mb-6">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#efe7dd' }}>
                <Check className="h-8 w-8" style={{ color: '#a0897c' }} />
              </div>
              <h2 className="text-2xl font-bold mb-2" style={{ color: '#837069' }}>
                Bedankt!
              </h2>
              <p style={{ color: '#a0897c' }}>
                Je afspraak is succesvol ingepland.
              </p>
            </div>

            <div className="rounded-xl p-4 mb-6 text-left" style={{ backgroundColor: '#efe7dd' }}>
              <h3 className="font-semibold mb-2" style={{ color: '#837069' }}>Afspraak Details:</h3>
              <div className="space-y-1 text-sm" style={{ color: '#a0897c' }}>
                <p><strong>Service:</strong> {service.title}</p>
                <p><strong>Provider:</strong> {service.provider.name}</p>
                <p><strong>Datum:</strong> {availableDates.find(d => d.value === selectedDate)?.label}</p>
                <p><strong>Tijd:</strong> {availableTimeSlots.find(t => t.value === selectedTime)?.label}</p>
                <p><strong>Duur:</strong> {service.duration}</p>
              </div>
            </div>

            <div className="space-y-3">
              <Button 
                onClick={handleBookAnother}
                className="w-full text-white hover:opacity-90"
                style={{ backgroundColor: '#a0897c' }}
              >
                Boek Nog Een Afspraak
              </Button>
              <Button 
                onClick={handleGoBack}
                variant="outline"
                className="w-full hover:bg-white/50"
                style={{ borderColor: '#efe7dd', color: '#837069' }}
              >
                Terug naar Service
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f6f1eb' }}>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-lg" style={{ borderBottom: '1px solid #efe7dd' }}>
        <div className="px-6 py-4">
          <div className="flex items-center gap-4 mb-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleGoBack}
              className="p-2 hover:bg-white/50"
              style={{ color: '#837069' }}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-semibold" style={{ color: '#837069' }}>
                Afspraak Inplannen
              </h1>
              <p className="text-sm" style={{ color: '#b6a092' }}>
                Kies je gewenste datum en tijd
              </p>
            </div>
          </div>

          {/* Appointment Header */}
          <div className="rounded-lg p-4 mb-4" style={{ backgroundColor: '#efe7dd', border: '1px solid #e6dbd0' }}>
            <div className="text-center">
              <div className="mb-2">
                <h1 className="text-2xl font-light tracking-wider" style={{ color: '#837069' }}>
                  <span className="font-bold" style={{ color: '#a0897c' }}>Afspraak Boeken</span>
                </h1>
              </div>
              <p className="text-sm" style={{ color: '#b6a092' }}>
                Maak een afspraak met een woman on a mission
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-6 pb-24">
        {/* Title */}
        <div className="mb-6">
          <h2 className="text-3xl font-bold mb-2" style={{ color: '#837069' }}>
            Plan je afspraak
          </h2>
          <p style={{ color: '#b6a092' }}>
            Kies je gewenste datum en tijd voor de sessie.
          </p>
        </div>

        {/* Service Description Card */}
        <Card className="mb-6 border-0 shadow-lg bg-white/80 backdrop-blur-sm" style={{ border: '1px solid #efe7dd' }}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4 mb-4">
              <Avatar className="h-12 w-12">
                <AvatarImage src={service.provider.avatar} alt={service.provider.name} />
                <AvatarFallback style={{ backgroundColor: '#efe7dd', color: '#837069' }}>
                  {service.provider.name.split(' ').map(n => n[0]).join('')}
                </AvatarFallback>
              </Avatar>
              <div>
                <h3 className="font-semibold" style={{ color: '#837069' }}>{service.title}</h3>
                <p className="text-sm" style={{ color: '#b6a092' }}>met {service.provider.name}</p>
              </div>
            </div>

            <div className="flex items-center gap-4 text-sm" style={{ color: '#a0897c' }}>
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                <span>{service.duration}</span>
              </div>
              <div className="flex items-center gap-1">
                <span>€{service.price}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Booking Form */}
        <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm" style={{ border: '1px solid #efe7dd' }}>
          <CardHeader>
            <CardTitle className="text-xl" style={{ color: '#837069' }}>Selecteer Datum & Tijd</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Date Selection */}
            <div className="space-y-2">
              <Label htmlFor="date" className="font-medium flex items-center gap-2" style={{ color: '#837069' }}>
                <Calendar className="h-4 w-4" />
                Kies datum
              </Label>
              <Select value={selectedDate} onValueChange={setSelectedDate}>
                <SelectTrigger className="w-full" style={{ borderColor: '#d9cbbf', backgroundColor: '#efe7dd' }}>
                  <SelectValue placeholder="Selecteer een datum" />
                </SelectTrigger>
                <SelectContent style={{ backgroundColor: '#efe7dd', border: '1px solid #d9cbbf' }} className="z-[100]">
                  {availableDates.map((date) => (
                    <SelectItem key={date.value} value={date.value} style={{ backgroundColor: '#efe7dd' }}>
                      {date.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Time Selection */}
            <div className="space-y-2">
              <Label htmlFor="time" className="font-medium flex items-center gap-2" style={{ color: '#837069' }}>
                <Clock className="h-4 w-4" />
                Kies tijd
              </Label>
              <Select value={selectedTime} onValueChange={setSelectedTime}>
                <SelectTrigger className="w-full" style={{ borderColor: '#d9cbbf', backgroundColor: '#efe7dd' }}>
                  <SelectValue placeholder="Selecteer een tijd" />
                </SelectTrigger>
                <SelectContent style={{ backgroundColor: '#efe7dd', border: '1px solid #d9cbbf' }} className="z-[100]">
                  {availableTimeSlots.map((time) => (
                    <SelectItem key={time.value} value={time.value} style={{ backgroundColor: '#efe7dd' }}>
                      {time.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Comments */}
            <div className="space-y-2">
              <Label htmlFor="comments" className="font-medium flex items-center gap-2" style={{ color: '#837069' }}>
                <MessageSquare className="h-4 w-4" />
                Opmerkingen (optioneel)
              </Label>
              <Textarea 
                id="comments"
                placeholder="Eventuele speciale wensen of informatie die je wilt delen..."
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                className="min-h-[100px]"
                style={{ borderColor: '#efe7dd' }}
              />
            </div>

            {/* Confirm Button */}
            <Button 
              onClick={handleConfirmAppointment}
              disabled={!selectedDate || !selectedTime || isLoading}
              className="w-full py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 hover:opacity-90"
              style={{ backgroundColor: '#a0897c', color: 'white' }}
              size="lg"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Afspraak inplannen...
                </>
              ) : (
                'Bevestig afspraak'
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AppointmentCalendar;